<template>
  <div class="px-4 py-5 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <section class="admin-header mb-8">
        <div class="admin-hero">
          <p class="text-xs font-semibold uppercase text-bochas-rojo">
            Gestión interna
          </p>
          <h2 class="mt-1 text-2xl font-bold text-white sm:text-3xl">
            Panel administrativo
          </h2>
          <p v-if="userRoleLabel" class="mt-1 text-sm text-bochas-text-muted">
            {{ userRoleLabel }}
          </p>
        </div>

        <div class="flex flex-shrink-0 items-center gap-3">
          <button type="button" class="public-site-link" @click="goToPublicSite">
            <IconFbochas name="home" :size="18" />
            <span>Ir al sitio</span>
          </button>

          <div class="admin-account hidden min-w-0 items-center gap-3 sm:flex">
            <span class="admin-account__avatar">{{ userInitials }}</span>
            <div>
              <p class="text-xs font-semibold uppercase text-bochas-text-muted">
                Sesión activa
              </p>
              <p class="mt-1 truncate text-base font-bold text-white">
                {{ userName }}
              </p>
              <p class="mt-1 break-all text-sm text-bochas-text-muted">
                {{ user?.email || "Sin email cargado" }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section v-if="showEmptyState" class="card-fbochas p-6 text-center">
        <p class="text-base text-white">Tu panel se está construyendo.</p>
        <p class="mt-1 text-sm text-bochas-text-muted">
          Cuándo esté listo vas a ver acá las acciones disponibles para tu rol
          ({{ userRoleLabel }}).
        </p>
      </section>

      <div v-else class="grid gap-4 lg:grid-cols-2">
        <section v-for="group in dashboardGroups" :key="group.key" class="admin-group-card">
          <div class="admin-group-heading">
            <span class="admin-group-heading__icon">
              <IconFbochas :name="group.icon" :size="17" />
            </span>
            <h3 class="text-sm font-bold uppercase tracking-wide text-white">
              {{ group.label }}
            </h3>
            <span class="ml-auto text-xs font-semibold text-bochas-text-muted">
              {{ group.items.length }}
            </span>
          </div>

          <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <router-link
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="admin-action-card group"
            >
              <span class="admin-action-card__icon" :class="item.iconClass">
                <IconFbochas :name="item.icon" :size="21" />
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-bold leading-tight text-white">
                  {{ item.title }}
                </span>
              </span>
              <IconFbochas name="arrow-right" :size="16" class="admin-action-card__arrow" />
            </router-link>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { getStoredUser, getMe } from "@/services/auth/authService";
import { ROLES, hasAnyRole, isGeneralAdmin, roleLabel } from "@/utils/roles";
import {
  crearAdminAccess,
  obtenerAdminAccess,
  obtenerModuloAdminPorRuta,
  puedeVerModulo,
} from "@/services/admin/adminModulesService";

const router = useRouter();
const user = ref(getStoredUser());
const adminAccess = ref(crearAdminAccess());

// Cada card declara qué roles pueden verla.
// Por ahora el filtro es por rol; el filtro fino por asociacionId/clubId
// se hace en los controllers del backend (próxima iteración).
const allItems = [
  {
    group: "contenido",
    title: "Noticias",
    description: "Novedades, comunicados y galerías.",
    to: "/admin/noticias",
    icon: "newspaper",
    iconClass: "admin-action-card__icon--red",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN],
  },
  {
    group: "contenido",
    title: "Historias",
    description: "Historias y notas destacadas.",
    to: "/admin/historias",
    icon: "doc",
    iconClass: "admin-action-card__icon--red",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    group: "contenido",
    title: "Notas",
    description: "Editá notas cortas y apuntes editoriales.",
    to: "/admin/notas",
    icon: "edit",
    iconClass: "admin-action-card__icon--gold",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
    hidden: true,
  },
  {
    group: "competencias",
    title: "Calendario / Próximas fechas",
    description: "Próximos torneos y fechas.",
    to: "/admin/calendario",
    icon: "calendarClock",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN],
  },
  {
    group: "competencias",
    title: "Resultados / Podios",
    description: "Clasificaciones, podios y fotos.",
    to: "/admin/resultados",
    icon: "trophy",
    iconClass: "admin-action-card__icon--gold",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    group: "competencias",
    title: "Categorías de torneos",
    description: "Catálogo de categorías disponibles.",
    to: "/admin/categorias",
    icon: "categories",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN],
  },
  {
    group: "contenido",
    title: "Pasado y Futuro",
    description: "Jugadores Históricos y Jóvenes Bochófilos.",
    to: "/admin/pasado-futuro",
    icon: "hourglass",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    group: "instituciones",
    title: "Asociaciones",
    description: "Asociaciones regionales y sus clubes.",
    to: "/admin/asociaciones",
    icon: "location",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN],
  },
  {
    group: "contenido",
    title: "Alojamiento",
    description: "Textos, imágenes, comodidades y tarifas.",
    to: "/admin/alojamiento",
    icon: "alojamiento",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    group: "contenido",
    title: "Portadas",
    description: "Imágenes principales del sitio.",
    to: "/admin/portadas",
    icon: "camara",
    iconClass: "admin-action-card__icon--gold",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    group: "administracion",
    title: "Configuración",
    description: "Redes sociales y SEO del sitio.",
    to: "/admin/configuracion",
    icon: "settings",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    group: "instituciones",
    title: "La Federación",
    description: "Autoridades y reglamentaciones.",
    to: "/admin/federacion",
    icon: "shield",
    iconClass: "admin-action-card__icon--red",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    group: "instituciones",
    title: "Clubes",
    description: "Clubes asociados de la provincia.",
    to: "/admin/clubes",
    icon: "users",
    iconClass: "admin-action-card__icon--gold",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    group: "instituciones",
    title: "Provincias",
    description: "Provincias disponibles para las instituciones.",
    to: "/admin/provincia",
    icon: "map",
    iconClass: "admin-action-card__icon--red",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    group: "instituciones",
    title: "Mi Club",
    description: "Información, logo y galería.",
    to: "/admin/mi-club",
    icon: "casa",
    iconClass: "admin-action-card__icon--red",
    roles: [ROLES.CLUB_ADMIN],
  },
  {
    group: "administracion",
    title: "Usuarios",
    description: "Accesos internos y contraseñas.",
    to: "/admin/usuarios",
    icon: "users",
    iconClass: "admin-action-card__icon--red",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    group: "administracion",
    title: "Importar datos",
    description: "Carga de archivos CSV o JSON.",
    to: "/admin/importar",
    icon: "upload",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
];

const dashboardItems = computed(() =>
  allItems.filter(
    (item) =>
      !item.hidden &&
      hasAnyRole(user.value, ...item.roles) &&
      (!item.requiresGeneralAdmin || isGeneralAdmin(user.value)) &&
      puedeVerModulo(
        adminAccess.value,
        obtenerModuloAdminPorRuta(item.to),
        user.value,
      ),
  ),
);

const groupDefinitions = [
  { key: "competencias", label: "Competencias", icon: "trophy" },
  { key: "contenido", label: "Contenido", icon: "edit" },
  { key: "instituciones", label: "Instituciones", icon: "landmark" },
  { key: "administracion", label: "Administración", icon: "settings" },
];

const dashboardGroups = computed(() =>
  groupDefinitions
    .map((group) => ({
      ...group,
      items: dashboardItems.value.filter((item) => item.group === group.key),
    }))
    .filter((group) => group.items.length),
);

const userName = computed(() => {
  if (!user.value) return "Administrador";
  const fullName = [user.value.firstname, user.value.lastname]
    .filter(Boolean)
    .join(" ");
  return fullName || "Administrador";
});

const firstName = computed(() => user.value?.firstname || "admin");

const userInitials = computed(() => {
  const initials = userName.value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return initials || "A";
});

const userRoleLabel = computed(() => roleLabel(user.value?.role, user.value));

const showEmptyState = computed(() => dashboardItems.value.length === 0);

async function goToPublicSite() {
  sessionStorage.removeItem("web-bochas-home-section");
  await router.push("/");
  await nextTick();
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
}

onMounted(async () => {
  if (!user.value) {
    router.replace("/login");
    return;
  }
  const fresh = await getMe();
  if (!fresh) {
    router.replace("/login");
  } else {
    user.value = fresh;
  }
  adminAccess.value = await obtenerAdminAccess();
});
</script>

<style scoped>
.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0 0.75rem;
}

.admin-hero {
  min-width: 0;
}

.admin-account {
  max-width: 21rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  padding: 0.55rem 0.9rem 0.55rem 0.55rem;
}

.admin-account__avatar {
  display: inline-flex;
  height: 2.5rem;
  width: 2.5rem;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(165, 33, 33, 0.95), rgba(29, 82, 124, 0.85));
  color: white;
  font-size: 0.8rem;
  font-weight: 800;
}

.public-site-link {
  display: inline-flex;
  height: 2.75rem;
  flex: none;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.06);
  padding: 0 0.9rem;
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
  transition: background-color 180ms ease, border-color 180ms ease;
}

.public-site-link:hover {
  border-color: rgba(165, 33, 33, 0.45);
  background: rgba(165, 33, 33, 0.14);
}

.admin-action-card {
  position: relative;
  display: grid;
  grid-template-columns: 2.25rem minmax(0, 1fr) 1rem;
  align-items: center;
  gap: 0.7rem;
  min-height: 3.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  background: #1a1f2e;
  padding: 0.65rem 0.75rem;
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 220ms ease, background-color 220ms ease;
}

.admin-group-heading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.admin-group-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
  padding: 1rem;
}

.admin-group-heading__icon {
  display: inline-flex;
  height: 2rem;
  width: 2rem;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
}

.admin-action-card:hover {
  transform: translateY(-3px);
  border-color: rgba(165, 33, 33, 0.45);
  background: #20263a;
}

.admin-action-card__icon {
  display: inline-flex;
  height: 2.25rem;
  width: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
}

@media (max-width: 639px) {
  .admin-header {
    align-items: flex-start;
    flex-wrap: wrap;
    padding-bottom: 1rem;
  }

  .admin-action-card {
    min-height: 3.75rem;
    padding: 0.65rem 0.75rem;
  }
}

.admin-action-card__icon--red {
  background: rgba(165, 33, 33, 0.18);
  color: #ffb7b7;
}

.admin-action-card__icon--blue {
  background: rgba(29, 82, 124, 0.24);
  color: #9fd5ff;
}

.admin-action-card__icon--gold {
  background: rgba(255, 185, 0, 0.16);
  color: #ffd66b;
}

.admin-action-card__arrow {
  color: rgba(255, 255, 255, 0.45);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), color 220ms ease;
}

.admin-action-card:hover .admin-action-card__arrow {
  color: white;
  transform: translateX(4px);
}

@media (prefers-reduced-motion: reduce) {
  .admin-action-card,
  .admin-action-card__arrow {
    transition: none;
  }

  .admin-action-card:hover,
  .admin-action-card:hover .admin-action-card__arrow {
    transform: none;
  }
}
</style>
